import { type Bbox, type Poligono, rectanguloTocaPoligono } from './geometria';

/**
 * Teselado de una zona en cuadrados de 1 km alineados a la malla kilometrica del huso.
 * El WFS INSPIRE del Catastro no admite mas de 1 km2 por peticion; alinear las teselas a la
 * malla hace que el identificador sea estable y que dos zonas que se solapan compartan
 * teselas ya descargadas.
 */
export interface Tesela {
  id: string;
  epsg: number;
  minx: number;
  miny: number;
  maxx: number;
  maxy: number;
}

export const LADO_TESELA_M = 1000;

export function idTesela(epsg: number, minx: number, miny: number, lado = LADO_TESELA_M): string {
  return `${epsg}_${Math.round(minx / lado)}_${Math.round(miny / lado)}`;
}

/** Teselas que cubren la caja (en coordenadas del huso). */
export function teselarBbox(bbox: Bbox, epsg: number, lado = LADO_TESELA_M): Tesela[] {
  const [minx, miny, maxx, maxy] = bbox;
  const i0 = Math.floor(minx / lado), i1 = Math.floor((maxx - 1e-6) / lado);
  const j0 = Math.floor(miny / lado), j1 = Math.floor((maxy - 1e-6) / lado);
  const res: Tesela[] = [];
  for (let j = j0; j <= j1; j++) {
    for (let i = i0; i <= i1; i++) {
      const x = i * lado, y = j * lado;
      res.push({ id: idTesela(epsg, x, y, lado), epsg, minx: x, miny: y, maxx: x + lado, maxy: y + lado });
    }
  }
  return res;
}

/** Teselas que tocan un poligono (en coordenadas del huso). */
export function teselarPoligono(poligono: Poligono, epsg: number, lado = LADO_TESELA_M): Tesela[] {
  if (poligono.length === 0 || poligono[0].length < 3) return [];
  let minx = Infinity, miny = Infinity, maxx = -Infinity, maxy = -Infinity;
  for (const [x, y] of poligono[0]) {
    if (x < minx) minx = x; if (y < miny) miny = y; if (x > maxx) maxx = x; if (y > maxy) maxy = y;
  }
  return teselarBbox([minx, miny, maxx, maxy], epsg, lado).filter((t) =>
    rectanguloTocaPoligono([t.minx, t.miny, t.maxx, t.maxy], poligono));
}

/** Bbox en texto para el parametro `bbox=` del WFS (minx,miny,maxx,maxy). */
export function bboxTesela(t: Tesela): string {
  return `${t.minx},${t.miny},${t.maxx},${t.maxy}`;
}

/**
 * Estimacion del volumen de descarga a partir de las medidas de requisitos.md (§5.2):
 * ~3,5 MB/km2 de parcelas y ~7,5 MB/km2 de edificios en ciudad densa. Se aplica un factor
 * de densidad (1 = centro de ciudad, 0,2 = chalets) que el usuario puede ajustar.
 */
export function estimarDescargaMb(nTeselas: number, capas: ('cp' | 'bu' | 'bp')[], densidad = 0.5): number {
  const porKm2: Record<'cp' | 'bu' | 'bp', number> = { cp: 3.5, bu: 7.5, bp: 28 };
  return nTeselas * densidad * capas.reduce((s, c) => s + porKm2[c], 0);
}
